import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';

export interface TimezoneOption {
  /** `country_tzones.tzoneID`. `0` means "Account Default". */
  value: number;
  /** Human-friendly label (e.g. "Pacific Time (Los Angeles)"). */
  label: string;
  /** IANA name (e.g. "America/Los_Angeles"). `null` for Account Default. */
  tzName: string | null;
}

interface ApiResponse<T> {
  data: T;
}

/**
 * Country-scoped timezone options for the in-app timezone picker.
 *
 * Wraps `GET /api/v5/timezones` (handled by `TimezonesController::index`),
 * which filters by the account's current country. The returned list
 * always includes a `value: 0` "Account Default" option that maps the
 * user back onto `aa_accounts.timezone`.
 *
 * Cached for an hour: country tzone catalog rarely changes during a
 * session and re-fetching on every popover open would be wasteful.
 */
export function useTimezones() {
  return useQuery({
    queryKey: ['timezones'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<TimezoneOption[]>>('/timezones');
      return response.data?.data ?? [];
    },
    staleTime: 60 * 60_000,
    gcTime: 120 * 60_000,
  });
}
